import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { pricesApi } from "@/api/prices";
import { fmt, hourFromTs, num, todayIso } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

function shiftDate(iso: string, deltaDays: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

export function RdnPage() {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso());
  const [applyFee, setApplyFee] = useState(true);

  const detail = useQuery({
    queryKey: ["rdn-day-detail", date],
    queryFn: () => pricesApi.rdnDayDetail(date),
  });
  const imsp = useQuery({
    queryKey: ["imbalance-prices", date],
    queryFn: () => pricesApi.imbalance(date),
  });

  const traderFeePct = num(detail.data?.trader_fee_pct ?? null) ?? 0;
  // Множник для отриманої ціни виробника: при увімкненому перемикачі ми
  // показуємо ціну вже за вирахуванням комісії трейдера (КГУ як продавець).
  const feeMul = applyFee && traderFeePct > 0 ? 1 - traderFeePct / 100 : 1;

  const chartData = useMemo(() => {
    const hours = detail.data?.hours ?? [];
    const imspByHour = new Map<number, number>();
    (imsp.data ?? []).forEach((p) => {
      const h = hourFromTs(p.ts);
      const v = num(p.price_uah_per_mwh);
      if (v !== null) imspByHour.set(h, v);
    });
    return hours.map((p) => {
      const h = hourFromTs(p.ts);
      const fact = num(p.price_uah_per_mwh);
      const fc = num(p.forecast_uah_per_mwh);
      return {
        hour: String(h).padStart(2, "0"),
        rdn: fact === null ? null : fact * feeMul,
        forecast: fc === null ? null : fc * feeMul,
        imsp: imspByHour.get(h) ?? null,
      };
    });
  }, [detail.data, imsp.data, feeMul]);

  const stats = useMemo(() => {
    const vals = chartData.map((r) => r.rdn).filter((v): v is number => v !== null && v > 0);
    if (vals.length === 0) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    return { min, max, avg };
  }, [chartData]);

  const hasAnyFact = chartData.some((r) => r.rdn !== null);
  const lastUpdated = detail.data?.last_updated_at;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle>{t("rdn.title")}</CardTitle>
              <CardDescription>{t("rdn.subtitle")}</CardDescription>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              {traderFeePct > 0 && (
                <button
                  type="button"
                  onClick={() => setApplyFee((v) => !v)}
                  title={t("rdn.fee_hint", { pct: traderFeePct })}
                  className={
                    "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors " +
                    (applyFee
                      ? "border-brand-mint-300 bg-brand-mint-50 text-brand-mint-700"
                      : "border-slate-200 bg-white text-slate-500 hover:text-slate-700")
                  }
                >
                  {applyFee ? "✓ " : ""}
                  {t("rdn.fee_toggle", { pct: traderFeePct })}
                </button>
              )}
              {lastUpdated && (
                <span>
                  {t("rdn.last_updated")}:{" "}
                  <span className="font-mono text-slate-700">
                    {new Date(lastUpdated).toLocaleTimeString("uk-UA", {
                      timeZone: "Europe/Kyiv",
                    })}
                  </span>
                </span>
              )}
              <a
                href="https://www.oree.com.ua/index.php/pricectr"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
              >
                oree.com.ua →
              </a>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 w-fit">
            <Label>{t("plan.date")}</Label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDate((d) => shiftDate(d, -1))}
                aria-label={t("rdn.prev_day")}
                title={t("rdn.prev_day")}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-[160px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDate((d) => shiftDate(d, 1))}
                aria-label={t("rdn.next_day")}
                title={t("rdn.next_day")}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <Stat label={t("rdn.min")} value={fmt(stats.min)} />
          <Stat label={t("rdn.avg")} value={fmt(stats.avg)} />
          <Stat label={t("rdn.max")} value={fmt(stats.max)} />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("rdn.chart_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {detail.isLoading ? (
            <div className="text-sm text-slate-500">{t("common.loading")}</div>
          ) : !hasAnyFact ? (
            <div className="text-sm text-slate-500">{t("rdn.not_published")}</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Bar dataKey="rdn" name={t("rdn.price")} fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {chartData.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="p-2">{t("common.hour")}</th>
                    <th className="p-2 text-right">{t("rdn.price")}</th>
                    <th className="p-2 text-right">{t("rdn.forecast")}</th>
                    <th className="p-2 text-right">{t("rdn.imsp")}</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((r) => (
                    <tr key={r.hour} className="border-t border-slate-100">
                      <td className="p-2 font-mono">{r.hour}:00</td>
                      <td className="p-2 text-right font-mono">
                        {r.rdn === null ? <span className="text-slate-400">—</span> : fmt(r.rdn)}
                      </td>
                      <td className="p-2 text-right font-mono text-slate-500">
                        {r.forecast === null ? "—" : fmt(r.forecast)}
                      </td>
                      <td className="p-2 text-right font-mono text-slate-500">
                        {r.imsp === null ? "—" : fmt(r.imsp)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-mono font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
